import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '@/App';
import { 
  Package, FileText, Receipt, Users, TicketIcon, TrendingUp,
  Calendar as CalendarIcon, Clock, CheckCircle, Circle, ArrowRight, Building2,
  AlertCircle, AlertTriangle, Code, FileCheck
} from 'lucide-react';
import { format, parseISO, isToday, isFuture } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [todayActions, setTodayActions] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [urgentTickets, setUrgentTickets] = useState([]);
  const [qaTickets, setQaTickets] = useState([]);
  const [devTickets, setDevTickets] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [todayTeamupEvents, setTodayTeamupEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingQA, setLoadingQA] = useState(true);
  const [loadingDev, setLoadingDev] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [loadingTeamup, setLoadingTeamup] = useState(true);

  useEffect(() => {
    // Fetch data based on user profile
    // Admin users can see all widgets, otherwise check profile and permissions
    const isAdmin = user?.role === 'admin';
    
    if (user?.profile === 'comercial' && (user?.permissions?.crm || isAdmin)) {
      fetchTodayActions();
      fetchActionTypes();
    }
    if (user?.profile === 'soporte' && (user?.permissions?.ticketing || isAdmin)) {
      fetchUrgentTickets();
    }
    if (user?.profile === 'qa' && (user?.permissions?.ticketing || isAdmin)) {
      fetchQATickets();
    }
    if (user?.profile === 'desarrollo' && (user?.permissions?.ticketing || isAdmin)) {
      fetchDevTickets();
    }
    if (user?.profile === 'administracion' && (user?.permissions?.facturacion || isAdmin)) {
      fetchPendingInvoices();
    }
    // Fetch teamup events for users with teamup permission
    if (user?.permissions?.teamup || isAdmin) {
      fetchTodayTeamupEvents();
    }
  }, [user]);

  const fetchTodayActions = async () => {
    try {
      // Rango del dia de hoy. OJO: setHours() MUTA el Date original,
      // asi que creamos dos objetos independientes para start y end.
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const startOfDay = start.toISOString();
      const endOfDay = end.toISOString();

      const response = await axiosInstance.get(
        `/actions?user_id=${user.id}&date_from=${startOfDay}&date_to=${endOfDay}`
      );
      setTodayActions(response.data);
    } catch (error) {
      console.error('Error loading today actions');
    } finally {
      setLoading(false);
    }
  };

  const fetchActionTypes = async () => {
    try {
      const response = await axiosInstance.get('/action-types');
      setActionTypes(response.data);
    } catch (error) {
      console.error('Error loading action types');
    }
  };

  const fetchUrgentTickets = async () => {
    try {
      const response = await axiosInstance.get('/tickets/sla-status');
      const today = new Date();
      
      // Filter tickets that are:
      // 1. Not closed
      // 2. Priority is 'urgent' or 'high'
      // 3. Created today OR have SLA due today
      const urgentToday = response.data.filter(ticket => {
        if (ticket.status === 'closed') return false;
        if (!['urgent', 'high'].includes(ticket.priority)) return false;
        
        const createdDate = new Date(ticket.created_at);
        const isCreatedToday = isToday(createdDate);
        
        let isDueToday = false;
        if (ticket.sla_due_date) {
          const dueDate = new Date(ticket.sla_due_date);
          isDueToday = isToday(dueDate);
        }
        
        return isCreatedToday || isDueToday;
      });
      
      setUrgentTickets(urgentToday);
    } catch (error) {
      console.error('Error loading urgent tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchQATickets = async () => {
    try {
      const response = await axiosInstance.get('/tickets/sla-status');
      
      // Filter tickets assigned to QA department
      const qaTickets = response.data.filter(ticket => {
        if (ticket.status === 'closed') return false;
        const deptName = ticket.current_department_name?.toLowerCase() || '';
        return deptName.includes('qa') || deptName.includes('calidad');
      });
      
      setQaTickets(qaTickets);
    } catch (error) {
      console.error('Error loading QA tickets:', error);
    } finally {
      setLoadingQA(false);
    }
  };

  const fetchDevTickets = async () => {
    try {
      const response = await axiosInstance.get('/tickets/sla-status');
      
      // Filter tickets assigned to Development department
      const devTickets = response.data.filter(ticket => {
        if (ticket.status === 'closed') return false;
        const deptName = ticket.current_department_name?.toLowerCase() || '';
        return deptName.includes('desarrollo') || deptName.includes('development');
      });
      
      setDevTickets(devTickets);
    } catch (error) {
      console.error('Error loading development tickets:', error);
    } finally {
      setLoadingDev(false);
    }
  };

  const fetchPendingInvoices = async () => {
    try {
      const response = await axiosInstance.get('/invoices');
      
      // Filter pending/unpaid invoices
      const pending = response.data.filter(invoice => {
        return invoice.payment_status !== 'paid';
      });
      
      setPendingInvoices(pending);
    } catch (error) {
      console.error('Error loading pending invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchTodayTeamupEvents = async () => {
    try {
      const response = await axiosInstance.get('/teamup/events');
      const today = new Date();
      
      // Filter events that are happening today or assigned to current user
      const eventsToday = response.data.filter(event => {
        const startDate = new Date(event.start_date);
        const endDate = new Date(event.end_date);
        
        // Check if event is today
        const isTodayEvent = isToday(startDate) || isToday(endDate) || 
          (startDate <= today && endDate >= today);
        
        // Show events assigned to user or all if not assigned
        const isRelevant = !event.assigned_to_user_id || event.assigned_to_user_id === user?.id;
        
        return isTodayEvent && isRelevant && event.status !== 'cancelled';
      });
      
      // Sort by start date
      eventsToday.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      
      setTodayTeamupEvents(eventsToday);
    } catch (error) {
      console.error('Error loading teamup events:', error);
    } finally {
      setLoadingTeamup(false);
    }
  };

  const handleActionClick = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  const handleTeamupEventClick = (eventId) => {
    navigate(`/teamup`);
  };

  const getActionTypeColor = (actionTypeId) => {
    const actionType = actionTypes.find(t => t.id === actionTypeId);
    return actionType?.color || 'gray';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', class: 'bg-yellow-100 text-yellow-800', icon: Circle },
      completed: { label: 'Completada', class: 'bg-green-100 text-green-800', icon: CheckCircle },
      not_done: { label: 'No Realizada', class: 'bg-red-100 text-red-800', icon: Circle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={config.class}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const stats = [
    { title: 'Productos Activos', value: '0', icon: Package, color: 'from-[#9C0F17] to-[#7A0C12]' },
    { title: 'Presupuestos', value: '0', icon: FileText, color: 'from-[#212B35] to-[#2a3745]' },
    { title: 'Facturas Pendientes', value: '0', icon: Receipt, color: 'from-orange-500 to-red-500' },
    { title: 'Leads Activos', value: '0', icon: Users, color: 'from-green-500 to-emerald-500' },
    { title: 'Tickets Abiertos', value: '0', icon: TicketIcon, color: 'from-yellow-500 to-orange-500' },
    { title: 'Conversión', value: '0%', icon: TrendingUp, color: 'from-indigo-500 to-purple-500' },
  ];

  const pendingActions = todayActions.filter(a => a.status === 'pending');
  const completedActions = todayActions.filter(a => a.status === 'completed');

  return (
    <Layout user={user}>
      <div className="space-y-8" data-testid="dashboard-container">
        <div>
          <h1 className="text-4xl font-bold text-[#212B35] mb-2" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-[#212B35]/70">Bienvenido, {user?.name || 'Usuario'}</p>
        </div>

        {/* CRM Actions Widget - Solo perfil Comercial */}
        {user?.profile === 'comercial' && (user?.permissions?.crm || user?.role === 'admin') && (
          <Card className="shadow-lg border-[#9C0F17]/20 bg-gradient-to-br from-white to-red-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-[#9C0F17]">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-[#212B35]">Mis Acciones de Hoy</CardTitle>
                    <p className="text-sm text-gray-600">
                      {format(new Date(), "EEEE d 'de' MMMM", { locale: es })}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/calendar')}
                  variant="outline"
                  size="sm"
                  className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
                >
                  Ver Calendario
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Cargando acciones...</div>
              ) : todayActions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No tienes acciones programadas para hoy</p>
                  <Button
                    onClick={() => navigate('/calendar')}
                    variant="outline"
                    size="sm"
                  >
                    Planificar acciones
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-2xl font-bold text-[#212B35]">{todayActions.length}</p>
                      <p className="text-xs text-gray-600">Total</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-2xl font-bold text-yellow-700">{pendingActions.length}</p>
                      <p className="text-xs text-gray-600">Pendientes</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-2xl font-bold text-green-700">{completedActions.length}</p>
                      <p className="text-xs text-gray-600">Completadas</p>
                    </div>
                  </div>

                  {/* Actions List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {todayActions
                      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                      .map((action) => {
                        const color = getActionTypeColor(action.action_type_id);
                        return (
                          <div
                            key={action.id}
                            className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer"
                            onClick={() => handleActionClick(action.client_id)}
                          >
                            <div className={`p-2 rounded-lg bg-${color}-100 flex-shrink-0`}>
                              <Clock className={`w-4 h-4 text-${color}-600`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm truncate">{action.title}</span>
                                <Badge className={`bg-${color}-100 text-${color}-800 text-xs`}>
                                  {action.action_type_name}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(parseISO(action.scheduled_date), 'HH:mm')}
                                </span>
                                <span className="flex items-center gap-1 truncate">
                                  <Building2 className="w-3 h-3" />
                                  {action.client_name}
                                </span>
                              </div>
                            </div>
                            {getStatusBadge(action.status)}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Urgent Tickets Widget - Solo perfil Soporte */}
        {user?.profile === 'soporte' && (user?.permissions?.ticketing || user?.role === 'admin') && (
          <Card className="shadow-lg border-orange-500/20 bg-gradient-to-br from-white to-orange-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500 to-red-500">
                    <TicketIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-[#212B35]">Tickets Urgentes de Hoy</CardTitle>
                    <p className="text-sm text-gray-600">
                      Prioridad Alta y Urgente
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/tickets')}
                  variant="outline"
                  size="sm"
                  className="border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white"
                >
                  Ver Todos
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTickets ? (
                <div className="text-center py-8 text-gray-500">Cargando tickets...</div>
              ) : urgentTickets.length === 0 ? (
                <div className="text-center py-8">
                  <TicketIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No hay tickets urgentes para hoy</p>
                  <p className="text-sm text-gray-500">¡Buen trabajo! 🎉</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-2xl font-bold text-[#212B35]">{urgentTickets.length}</p>
                      <p className="text-xs text-gray-600">Total Hoy</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-700">
                        {urgentTickets.filter(t => t.sla_info?.is_breached).length}
                      </p>
                      <p className="text-xs text-gray-600">SLA Incumplido</p>
                    </div>
                  </div>

                  {/* Tickets List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {urgentTickets
                      .sort((a, b) => {
                        // Sort by SLA status first (breached first), then by due date
                        if (a.sla_info?.is_breached !== b.sla_info?.is_breached) {
                          return a.sla_info?.is_breached ? -1 : 1;
                        }
                        if (!a.sla_due_date) return 1;
                        if (!b.sla_due_date) return -1;
                        return new Date(a.sla_due_date) - new Date(b.sla_due_date);
                      })
                      .map((ticket) => {
                        const priorityConfig = {
                          urgent: { 
                            label: 'Urgente', 
                            bgClass: 'bg-red-100 border-red-300',
                            textClass: 'text-red-800',
                            badgeClass: 'bg-red-500'
                          },
                          high: { 
                            label: 'Alta', 
                            bgClass: 'bg-orange-100 border-orange-300',
                            textClass: 'text-orange-800',
                            badgeClass: 'bg-orange-500'
                          }
                        };
                        const config = priorityConfig[ticket.priority] || priorityConfig.high;
                        
                        const slaStatusConfig = {
                          breached: { label: 'Incumplido', class: 'bg-red-600 text-white' },
                          critical: { label: 'Crítico', class: 'bg-red-500 text-white' },
                          warning: { label: 'Alerta', class: 'bg-yellow-500 text-white' },
                          ok: { label: 'OK', class: 'bg-green-500 text-white' },
                          completed: { label: 'Cerrado', class: 'bg-gray-400 text-white' }
                        };
                        const slaConfig = slaStatusConfig[ticket.sla_info?.status] || slaStatusConfig.ok;
                        
                        return (
                          <div
                            key={ticket.id}
                            className={`flex items-center gap-3 p-3 border-2 rounded-lg hover:shadow-md transition-all cursor-pointer ${config.bgClass}`}
                            onClick={() => handleTicketClick(ticket.id)}
                          >
                            <div className={`p-2 rounded-lg ${config.badgeClass} flex-shrink-0`}>
                              <TicketIcon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm">{ticket.ticket_number}</span>
                                <Badge className={`${config.textClass} text-xs`}>
                                  {config.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-800 truncate mb-1">{ticket.title}</p>
                              <div className="flex items-center gap-3 text-xs text-gray-700">
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {ticket.customer_name}
                                </span>
                                {ticket.sla_due_date && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Vence: {format(parseISO(ticket.sla_due_date), 'HH:mm')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge className={`${slaConfig.class} text-xs px-2 py-1`}>
                              {slaConfig.label}
                            </Badge>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* QA Tickets Widget - Solo perfil QA */}
        {user?.profile === 'qa' && (user?.permissions?.ticketing || user?.role === 'admin') && (
          <Card className="shadow-lg border-blue-500/20 bg-gradient-to-br from-white to-blue-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500">
                    <FileCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-[#212B35]">Tickets de QA / Calidad</CardTitle>
                    <p className="text-sm text-gray-600">
                      Tickets asignados al departamento
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/tickets')}
                  variant="outline"
                  size="sm"
                  className="border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white"
                >
                  Ver Todos
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingQA ? (
                <div className="text-center py-8 text-gray-500">Cargando tickets...</div>
              ) : qaTickets.length === 0 ? (
                <div className="text-center py-8">
                  <FileCheck className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No hay tickets de QA pendientes</p>
                  <p className="text-sm text-gray-500">¡Todo bajo control! ✅</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-2xl font-bold text-[#212B35]">{qaTickets.length}</p>
                      <p className="text-xs text-gray-600">Pendientes</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-700">
                        {qaTickets.filter(t => t.sla_info?.is_breached).length}
                      </p>
                      <p className="text-xs text-gray-600">SLA Vencido</p>
                    </div>
                  </div>

                  {/* Tickets List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {qaTickets
                      .sort((a, b) => {
                        if (a.sla_info?.is_breached !== b.sla_info?.is_breached) {
                          return a.sla_info?.is_breached ? -1 : 1;
                        }
                        return new Date(b.created_at) - new Date(a.created_at);
                      })
                      .map((ticket) => {
                        const priorityConfig = {
                          urgent: { label: 'Urgente', badgeClass: 'bg-red-500' },
                          high: { label: 'Alta', badgeClass: 'bg-orange-500' },
                          medium: { label: 'Media', badgeClass: 'bg-yellow-500' },
                          low: { label: 'Baja', badgeClass: 'bg-green-500' }
                        };
                        const config = priorityConfig[ticket.priority] || priorityConfig.medium;
                        
                        return (
                          <div
                            key={ticket.id}
                            className="flex items-center gap-3 p-3 bg-white border-2 rounded-lg hover:shadow-md transition-all cursor-pointer hover:border-blue-500"
                            onClick={() => handleTicketClick(ticket.id)}
                          >
                            <div className={`p-2 rounded-lg ${config.badgeClass} flex-shrink-0`}>
                              <TicketIcon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm">{ticket.ticket_number}</span>
                                <Badge className="text-xs">{config.label}</Badge>
                              </div>
                              <p className="text-sm text-gray-800 truncate">{ticket.title}</p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Development Tickets Widget - Solo perfil Desarrollo */}
        {user?.profile === 'desarrollo' && (user?.permissions?.ticketing || user?.role === 'admin') && (
          <Card className="shadow-lg border-indigo-500/20 bg-gradient-to-br from-white to-indigo-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500">
                    <Code className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-[#212B35]">Tickets de Desarrollo</CardTitle>
                    <p className="text-sm text-gray-600">
                      Tickets asignados al departamento
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/tickets')}
                  variant="outline"
                  size="sm"
                  className="border-indigo-500 text-indigo-600 hover:bg-indigo-500 hover:text-white"
                >
                  Ver Todos
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingDev ? (
                <div className="text-center py-8 text-gray-500">Cargando tickets...</div>
              ) : devTickets.length === 0 ? (
                <div className="text-center py-8">
                  <Code className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No hay tickets de desarrollo pendientes</p>
                  <p className="text-sm text-gray-500">¡Sin bugs! 💻</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-2xl font-bold text-[#212B35]">{devTickets.length}</p>
                      <p className="text-xs text-gray-600">Pendientes</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-700">
                        {devTickets.filter(t => t.sla_info?.is_breached).length}
                      </p>
                      <p className="text-xs text-gray-600">SLA Vencido</p>
                    </div>
                  </div>

                  {/* Tickets List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {devTickets
                      .sort((a, b) => {
                        if (a.sla_info?.is_breached !== b.sla_info?.is_breached) {
                          return a.sla_info?.is_breached ? -1 : 1;
                        }
                        return new Date(b.created_at) - new Date(a.created_at);
                      })
                      .map((ticket) => {
                        const priorityConfig = {
                          urgent: { label: 'Urgente', badgeClass: 'bg-red-500' },
                          high: { label: 'Alta', badgeClass: 'bg-orange-500' },
                          medium: { label: 'Media', badgeClass: 'bg-yellow-500' },
                          low: { label: 'Baja', badgeClass: 'bg-green-500' }
                        };
                        const config = priorityConfig[ticket.priority] || priorityConfig.medium;
                        
                        return (
                          <div
                            key={ticket.id}
                            className="flex items-center gap-3 p-3 bg-white border-2 rounded-lg hover:shadow-md transition-all cursor-pointer hover:border-indigo-500"
                            onClick={() => handleTicketClick(ticket.id)}
                          >
                            <div className={`p-2 rounded-lg ${config.badgeClass} flex-shrink-0`}>
                              <TicketIcon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm">{ticket.ticket_number}</span>
                                <Badge className="text-xs">{config.label}</Badge>
                              </div>
                              <p className="text-sm text-gray-800 truncate">{ticket.title}</p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pending Invoices Widget - Solo perfil Administración */}
        {user?.profile === 'administracion' && (user?.permissions?.facturacion || user?.role === 'admin') && (
          <Card className="shadow-lg border-green-500/20 bg-gradient-to-br from-white to-green-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500">
                    <Receipt className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-[#212B35]">Facturas Pendientes</CardTitle>
                    <p className="text-sm text-gray-600">
                      Facturas sin pagar
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/invoices')}
                  variant="outline"
                  size="sm"
                  className="border-green-500 text-green-600 hover:bg-green-500 hover:text-white"
                >
                  Ver Todas
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingInvoices ? (
                <div className="text-center py-8 text-gray-500">Cargando facturas...</div>
              ) : pendingInvoices.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No hay facturas pendientes</p>
                  <p className="text-sm text-gray-500">¡Todas cobradas! 💰</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-2xl font-bold text-[#212B35]">{pendingInvoices.length}</p>
                      <p className="text-xs text-gray-600">Pendientes</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-2xl font-bold text-green-700">
                        {pendingInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0).toFixed(2)}€
                      </p>
                      <p className="text-xs text-gray-600">Total</p>
                    </div>
                  </div>

                  {/* Invoices List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {pendingInvoices
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((invoice) => {
                        const statusConfig = {
                          pending: { label: 'Pendiente', class: 'bg-yellow-100 text-yellow-800' },
                          partially_paid: { label: 'Parcial', class: 'bg-orange-100 text-orange-800' },
                          overdue: { label: 'Vencida', class: 'bg-red-100 text-red-800' }
                        };
                        const config = statusConfig[invoice.payment_status] || statusConfig.pending;
                        
                        return (
                          <div
                            key={invoice.id}
                            className="flex items-center gap-3 p-3 bg-white border-2 rounded-lg hover:shadow-md transition-all cursor-pointer hover:border-green-500"
                            onClick={() => navigate(`/invoices`)}
                          >
                            <div className="p-2 rounded-lg bg-green-100 flex-shrink-0">
                              <Receipt className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm">{invoice.invoice_number}</span>
                                <Badge className={config.class}>{config.label}</Badge>
                              </div>
                              <p className="text-sm text-gray-800 truncate">{invoice.customer_name}</p>
                              <p className="text-xs text-gray-600">{invoice.total?.toFixed(2)}€</p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Teamup Events Widget - For users with teamup permission */}
        {(user?.permissions?.teamup || user?.role === 'admin') && (
          <Card className="shadow-lg border-indigo-500/20 bg-gradient-to-br from-white to-indigo-50/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500">
                    <CalendarIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-[#212B35]">Eventos Teamup de Hoy</CardTitle>
                    <p className="text-sm text-gray-600">
                      Tus intervenciones y citas programadas
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/teamup')}
                  variant="outline"
                  size="sm"
                  className="border-indigo-500 text-indigo-600 hover:bg-indigo-500 hover:text-white"
                >
                  Ver Calendario
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTeamup ? (
                <div className="text-center py-8 text-gray-500">Cargando eventos...</div>
              ) : todayTeamupEvents.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">No tienes eventos programados para hoy</p>
                  <p className="text-sm text-gray-500">¡Día libre! 🎉</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-2xl font-bold text-[#212B35]">{todayTeamupEvents.length}</p>
                      <p className="text-xs text-gray-600">Total Hoy</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-2xl font-bold text-blue-700">
                        {todayTeamupEvents.filter(e => e.status === 'planned').length}
                      </p>
                      <p className="text-xs text-gray-600">Planificados</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-2xl font-bold text-yellow-700">
                        {todayTeamupEvents.filter(e => e.status === 'in_progress').length}
                      </p>
                      <p className="text-xs text-gray-600">En Curso</p>
                    </div>
                  </div>

                  {/* Events List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {todayTeamupEvents.map((event) => {
                      const statusConfig = {
                        planned: { label: 'Planificado', class: 'bg-blue-100 text-blue-800' },
                        in_progress: { label: 'En Curso', class: 'bg-yellow-100 text-yellow-800' },
                        completed: { label: 'Completado', class: 'bg-green-100 text-green-800' }
                      };
                      const config = statusConfig[event.status] || statusConfig.planned;
                      
                      return (
                        <div
                          key={event.id}
                          className="flex items-center gap-3 p-3 bg-white border-2 rounded-lg hover:shadow-md transition-all cursor-pointer hover:border-indigo-500"
                          onClick={() => handleTeamupEventClick(event.id)}
                          style={{ borderLeftWidth: '4px', borderLeftColor: event.category_color }}
                        >
                          <div 
                            className="p-2 rounded-lg flex-shrink-0"
                            style={{ backgroundColor: event.category_color + '20' }}
                          >
                            <CalendarIcon 
                              className="w-4 h-4" 
                              style={{ color: event.category_color }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm truncate">{event.title}</span>
                              <Badge 
                                className="text-xs"
                                style={{ backgroundColor: event.category_color, color: 'white' }}
                              >
                                {event.category_name}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {format(new Date(event.start_date), 'HH:mm')} - {format(new Date(event.end_date), 'HH:mm')}
                              </span>
                              {event.customer_name && (
                                <span className="flex items-center gap-1 truncate">
                                  <Building2 className="w-3 h-3" />
                                  {event.customer_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <Badge className={config.class}>{config.label}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="relative overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-[#212B35]/10" data-testid={`stat-card-${index}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`}></div>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#212B35]/70">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#212B35]" data-testid={`stat-value-${index}`}>{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Access */}
        <Card className="shadow-lg border-[#212B35]/10">
          <CardHeader>
            <CardTitle className="text-[#212B35]">Acceso Rápido</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {user?.permissions?.facturacion && (
                <>
                  <div 
                    className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                    onClick={() => navigate('/products')}
                  >
                    <Package className="w-8 h-8 text-[#9C0F17] mb-2" />
                    <h3 className="font-semibold text-[#212B35] mb-1">Productos</h3>
                    <p className="text-sm text-[#212B35]/70">Gestionar productos</p>
                  </div>
                  <div 
                    className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                    onClick={() => navigate('/quotes')}
                  >
                    <FileText className="w-8 h-8 text-[#9C0F17] mb-2" />
                    <h3 className="font-semibold text-[#212B35] mb-1">Presupuestos</h3>
                    <p className="text-sm text-[#212B35]/70">Crear presupuestos</p>
                  </div>
                  <div 
                    className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                    onClick={() => navigate('/invoices')}
                  >
                    <Receipt className="w-8 h-8 text-[#9C0F17] mb-2" />
                    <h3 className="font-semibold text-[#212B35] mb-1">Facturas</h3>
                    <p className="text-sm text-[#212B35]/70">Ver facturas</p>
                  </div>
                </>
              )}
              {user?.permissions?.crm && (
                <>
                  <div 
                    className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                    onClick={() => navigate('/clients')}
                  >
                    <Building2 className="w-8 h-8 text-[#9C0F17] mb-2" />
                    <h3 className="font-semibold text-[#212B35] mb-1">Clientes</h3>
                    <p className="text-sm text-[#212B35]/70">Gestionar clientes</p>
                  </div>
                  <div 
                    className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                    onClick={() => navigate('/crm')}
                  >
                    <Users className="w-8 h-8 text-[#9C0F17] mb-2" />
                    <h3 className="font-semibold text-[#212B35] mb-1">CRM</h3>
                    <p className="text-sm text-[#212B35]/70">Pipeline de ventas</p>
                  </div>
                  <div 
                    className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                    onClick={() => navigate('/calendar')}
                  >
                    <CalendarIcon className="w-8 h-8 text-[#9C0F17] mb-2" />
                    <h3 className="font-semibold text-[#212B35] mb-1">Calendario</h3>
                    <p className="text-sm text-[#212B35]/70">Ver mi agenda</p>
                  </div>
                </>
              )}
              {user?.permissions?.ticketing && (
                <div 
                  className="p-4 border-2 border-[#212B35]/10 rounded-lg hover:border-[#9C0F17] transition-colors cursor-pointer hover:bg-[#9C0F17]/5"
                  onClick={() => navigate('/tickets')}
                >
                  <TicketIcon className="w-8 h-8 text-[#9C0F17] mb-2" />
                  <h3 className="font-semibold text-[#212B35] mb-1">Tickets</h3>
                  <p className="text-sm text-[#212B35]/70">Gestionar tickets</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
